import React from 'react';
import styles from './OrganizationLogin.module.scss';
import ButtonLink from '@/components/ui/ButtonLink/ButtonLink.tsx';
import MicrosoftLogo from '@/assets/Icons/Microsoft-Logo-Color.svg?react';
import { useWhoamiStore } from '@/store/useWhoamiStore.ts';
import { Form, FormikHelpers, FormikProvider, useFormik } from 'formik';
import { useParams } from 'react-router-dom';
import { login, whoami } from '@/api/auth.ts';
import { useNavigate } from 'react-router';
import routes from '@/constants/routes.ts';
import Spinner from '@/components/ui/Spinner/Spinner.tsx';
import GoogleLogo from '@/assets/Icons/Google-Logo-Color.svg?react';
import OktaLogo from '@/assets/Icons/Okta-Logo-Color.svg?react';
import FormikInput from '@/components/Formik/FormikInput.tsx';
import FormikPasswordInput from '@/components/Formik/FormikPasswordInput.tsx';
import Button from '@/components/ui/Button/Button.tsx';
import { loginSchema } from '@/validations/auth.ts';
import handleResponseErrors from '@/utils/handleResponseErrors.ts';
import GlobalError from '@/components/FormikErrors/GlobalError.tsx';
import { useModalStore } from '@/store/useModalStore.ts';
import { isErrorMatching } from '@/utils/axiosUtil.ts';
import TwoFaModal from '@/pages/Authentication/Login/TwoFaModal/TwoFaModal.tsx';
import { activateAccount } from '@/api/user.ts';
import { activateAccountSchema } from '@/validations/user.ts';
import { routeAfterAuth } from '@/utils/authRedirect.ts';
import OrganizationLoginLayout from './OrganizationLoginLayout.tsx';
import useOrganizationLogin from './useOrganizationLogin.ts';
import CHCLoginPage from './CHCLoginPage/CHCLoginPage.tsx';
import LoginRequest = App.Data.Request.LoginRequest;

interface ActivateAccountValues {
	password: string;
	confirmPassword: string;
}

const DefaultOrganizationLogin: React.FC = () => {
	const { setWhoami } = useWhoamiStore(state => state);
	const { openModal, activeModal } = useModalStore();
	const navigate = useNavigate();
	const { data, isLoading, message, activationToken, handleSsoClick } = useOrganizationLogin();

	const finalizeAuthenticatedRouting = async () => {
		const whoamiData = await whoami();
		setWhoami(whoamiData);
		routeAfterAuth(whoamiData);
	};

	const handleSubmit = async (values: LoginRequest, formikHelpers: FormikHelpers<LoginRequest>) => {
		try {
			await login(values);
			await finalizeAuthenticatedRouting();
		} catch (error) {
			if (isErrorMatching(error, 'TwoFactorAuthenticationException', 401)) {
				openModal('twoFaModal');
				return;
			}

			handleResponseErrors(error, formikHelpers);
		}
	};

	const formik = useFormik({
		initialValues: {
			email: '',
			password: '',
		},
		validationSchema: loginSchema,
		onSubmit: handleSubmit,
	});

	const activationFormik = useFormik<ActivateAccountValues>({
		initialValues: {
			password: '',
			confirmPassword: '',
		},
		validationSchema: activateAccountSchema,
		onSubmit: async (values, formikHelpers) => {
			try {
				await activateAccount({
					password: values.password,
					token: activationToken,
				});
				await finalizeAuthenticatedRouting();
			} catch (error) {
				handleResponseErrors(error, formikHelpers);
			}
		},
	});

	const { isSubmitting } = formik;
	const showActivationForm = !!activationToken && !!data && !data.ssoRequired;

	if (isLoading) {
		return <Spinner />;
	}

	const ssoButtons = data?.ssoProviders?.map(ssoProvider => (
		<ButtonLink
			key={ssoProvider.id}
			fullWidth
			href={ssoProvider.action.url}
			color="default"
			hover={false}
			size="large"
			className={styles.ssoButton}
			onClick={handleSsoClick}
		>
			{ssoProvider.label.toLowerCase() === 'google' && <GoogleLogo />}
			{(ssoProvider.label.toLowerCase() === 'microsoft' || ssoProvider.label.toLowerCase() === 'azure') && (
				<MicrosoftLogo />
			)}
			{ssoProvider.label.toLowerCase() === 'okta' && <OktaLogo />} Continue with {ssoProvider.label}
		</ButtonLink>
	));

	const renderLoginCard = () => {
		if (data?.ssoRequired) {
			return (
				<>
					<h2>Log into Goodwings</h2>
					{ssoButtons}
				</>
			);
		}

		if (showActivationForm) {
			return (
				<>
					<h2>Activate your account</h2>
					<FormikProvider value={activationFormik}>
						<Form>
							<GlobalError />
							<div>
								<FormikPasswordInput
									name="password"
									placeholder="Password*"
									type="password"
									className={styles.formikInput}
								/>
								<FormikPasswordInput
									name="confirmPassword"
									placeholder="Confirm Password*"
									type="password"
									className={styles.formikInput}
								/>
								<Button
									fullWidth
									type="submit"
									color="darkGreen"
									size="large"
									loading={activationFormik.isSubmitting}
									disabled={activationFormik.isSubmitting}
									className={styles.submitbttn}
								>
									Activate my account
								</Button>
							</div>
						</Form>
					</FormikProvider>
				</>
			);
		}

		return (
			<>
				<h2>Log into Goodwings</h2>
				<FormikProvider value={formik}>
					<Form>
						<GlobalError />
						<div>
							<FormikInput name="email" placeholder="Email*" className={styles.formikInput} />
							<FormikPasswordInput
								name="password"
								placeholder="Password*"
								type="password"
								className={styles.formikInput}
							/>
							<button
								type="button"
								className={styles.forgotPassword}
								onClick={() => navigate(routes.forgotPassword)}
							>
								Forgot Password?
							</button>
							<Button
								fullWidth
								type="submit"
								color="darkGreen"
								size="large"
								loading={isSubmitting}
								disabled={isSubmitting}
							>
								Sign in
							</Button>
						</div>
					</Form>
				</FormikProvider>
				<p className={styles.or}>or</p>
				{ssoButtons}
			</>
		);
	};

	return (
		<>
			<OrganizationLoginLayout data={data} message={message}>
				<div className={styles.form}>{renderLoginCard()}</div>
			</OrganizationLoginLayout>
			{activeModal && <TwoFaModal formik={formik} />}
		</>
	);
};

const OrganizationLogin: React.FC = () => {
	const { organizationSlug } = useParams<{ organizationSlug: string }>();

	return organizationSlug === 'chc' ? <CHCLoginPage /> : <DefaultOrganizationLogin />;
};

export default OrganizationLogin;
